import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Car } from "lucide-react";
import { useVehicle } from "@/contexts/VehicleContext";

const LINKS = [
  { label: "Nettoyage", to: "/nettoyage" },
  { label: "Mécanique", to: "/mecanique" },
  { label: "Mobilier textile", to: "/textile" },
  { label: "Surfaces ext. & locaux", to: "/surfaces" },
  { label: "Avant / Après", to: "/galerie" },
  { label: "Avis", to: "/avis" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { category, openSelector } = useVehicle();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl backdrop-saturate-150 bg-black/60 border-b border-white/10 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.7)]"
          : "bg-transparent"
      }`}
    >
      <div className={`max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between transition-all duration-300 ${scrolled ? "h-16" : "h-20"}`}>
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <img
            src="/logo.png"
            alt="Nova Clean Service"
            className={`w-auto transition-all duration-300 group-hover:scale-105 ${scrolled ? "h-11 sm:h-12" : "h-14 sm:h-16"}`}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.slice(1)}`}
                className={`text-sm uppercase tracking-wider font-medium transition-colors relative group ${active ? 'text-white' : 'text-white/70 hover:text-white'}`}
              >
                {l.label}
                <span className={`absolute left-0 -bottom-1 h-[2px] bg-[#E50914] transition-all ${active ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {category ? (
            <button
              onClick={openSelector}
              data-testid="header-category-chip"
              className="inline-flex items-center gap-2 px-3 py-2 rounded border border-white/15 hover:border-[#E50914] bg-white/5 hover:bg-white/10 transition-all group"
            >
              <span className="w-7 h-7 rounded bg-[#E50914] flex items-center justify-center text-xs font-black text-white">
                {category}
              </span>
              <span className="text-xs uppercase tracking-wider font-semibold text-white/70 group-hover:text-white">Véhicule</span>
            </button>
          ) : (
            <button
              onClick={openSelector}
              data-testid="header-select-vehicle"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded border border-white/15 hover:border-white/30 text-sm font-semibold text-white/80 hover:text-white transition-all"
            >
              <Car className="w-4 h-4" /> Mon véhicule
            </button>
          )}
          <Link
            to="/nettoyage#reserver"
            data-testid="header-cta-button"
            className="btn-sheen inline-flex items-center gap-2 bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] text-white font-semibold text-sm uppercase tracking-wider px-5 py-2.5 rounded-lg border border-[#E50914] hover:shadow-[0_0_24px_rgba(229,9,20,0.55)] transition-all"
          >
            Réserver
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-white"
          onClick={() => setOpen(!open)}
          aria-label="menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl animate-fade-up">
          <nav className="flex flex-col py-4 px-6">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="py-3 text-white/80 hover:text-[#E50914] uppercase text-sm tracking-wider font-medium border-b border-white/5"
                data-testid={`mobile-nav-${l.to.slice(1)}`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/nettoyage#reserver"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-reserver"
              className="mt-4 inline-flex items-center justify-center gap-2 bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] text-white font-bold uppercase text-sm tracking-wider py-3 rounded"
            >
              Réserver
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
